import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceMonitoringPanel = ({ complianceStatus, loading, onRefresh, onExport }) => {
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const complianceMetrics = [
    {
      label: 'Data Retention Compliance',
      score: complianceStatus?.dataRetentionCompliance,
      description: 'PNG labor law requires 7-year data retention for employee records',
      icon: 'Archive'
    },
    {
      label: 'Privacy Policy Compliance',
      score: complianceStatus?.privacyPolicyCompliance,
      description: 'Data privacy and employee consent management',
      icon: 'Shield'
    },
    {
      label: 'Access Control Compliance',
      score: complianceStatus?.accessControlCompliance,
      description: 'Role-based access control and authorization',
      icon: 'Lock'
    },
    {
      label: 'Encryption Compliance',
      score: complianceStatus?.encryptionCompliance,
      description: 'Data encryption at rest and in transit',
      icon: 'Key'
    },
    {
      label: 'Audit Log Compliance',
      score: complianceStatus?.auditLogCompliance,
      description: 'Comprehensive audit trail for all data access',
      icon: 'FileText'
    }
  ];

  const getScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 95) return 'bg-green-50';
    if (score >= 80) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <Icon name="Shield" className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <span className="break-words">PNG Labor Law Compliance Monitoring</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button onClick={onExport} variant="outline" size="sm" className="whitespace-nowrap">
            <Icon name="Download" className="w-4 h-4" />
            <span className="hidden sm:inline ml-2">Export Report</span>
            <span className="sm:hidden ml-2">Export</span>
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm" className="whitespace-nowrap">
            <Icon name="RefreshCw" className="w-4 h-4" />
            <span className="hidden sm:inline ml-2">Refresh</span>
          </Button>
        </div>
      </div>
      {/* Overall Compliance Score */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 break-words">Overall Compliance Score</h3>
            <p className="text-xs sm:text-sm text-gray-600 break-words">Last audit: {new Date(complianceStatus?.lastAudit)?.toLocaleDateString()}</p>
          </div>
          <div className="text-center flex-shrink-0">
            <div className={`text-4xl sm:text-5xl font-bold ${getScoreColor(complianceStatus?.overallScore)}`}>
              {complianceStatus?.overallScore}%
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">Compliant</p>
          </div>
        </div>
      </div>
      {/* Compliance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        {complianceMetrics?.map((metric, index) => (
          <div key={index} className={`border border-gray-200 rounded-lg p-4 sm:p-6 ${getScoreBgColor(metric?.score)}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`p-2 rounded-lg ${getScoreColor(metric?.score)} bg-white`}>
                  <Icon name={metric?.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{metric?.label}</h4>
                  <p className="text-sm text-gray-600 mt-1">{metric?.description}</p>
                </div>
              </div>
              <span className={`text-2xl font-bold ${getScoreColor(metric?.score)}`}>
                {metric?.score}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  metric?.score >= 95 ? 'bg-green-600' : metric?.score >= 80 ? 'bg-yellow-600' : 'bg-red-600'
                }`}
                style={{ width: `${metric?.score}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
      {/* Compliance Violations */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" className="w-5 h-5 text-orange-600" />
          Compliance Violations
        </h3>
        {complianceStatus?.violations?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Violations Detected</h4>
            <p className="text-gray-600">All compliance requirements are being met according to PNG labor law standards.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {complianceStatus?.violations?.map((violation, index) => (
              <div key={index} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{violation?.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{violation?.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Detected: {new Date(violation?.detectedAt)?.toLocaleDateString()}</span>
                      <span>Severity: {violation?.severity}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-orange-600 border-orange-300">
                    Remediate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Regulatory Requirements */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="BookOpen" className="w-5 h-5 text-blue-600" />
          PNG Labor Law Requirements
        </h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <Icon name="Check" className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">7-Year Data Retention</p>
              <p className="text-sm text-gray-600">All employee records must be retained for minimum 7 years</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Encryption Standards</p>
              <p className="text-sm text-gray-600">AES-256 encryption required for sensitive employee data</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Access Control</p>
              <p className="text-sm text-gray-600">Role-based access control with audit logging for all data access</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Data Privacy</p>
              <p className="text-sm text-gray-600">Employee consent required for data collection and processing</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ComplianceMonitoringPanel;